import React, { useState, useEffect } from 'react';
import '../Styles/Announcement.css';
import NavBar from '../NavBar';
import API_BASE_URL from "../../config";

const Announcement = () => {
  // ----------------------------
  // States
  // ----------------------------
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState(["All Announcements"]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Announcements');

  // ----------------------------
  // Fetch Data
  // ----------------------------
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/announcements`);
        const data = await res.json();
        if (data.success) {
          setAnnouncements(data.data);
          // Extract categories dynamically
          const uniqueCategories = ["All Announcements", ...new Set(data.data.map(a => a.category))];
          setCategories(uniqueCategories);
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  // ----------------------------
  // Filter Announcements
  // ----------------------------
  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.author.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'All Announcements' ||
      announcement.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // ----------------------------
  // Helper Functions
  // ----------------------------
  const getCategoryColor = (category) => {
    switch (category) {
      case 'COMPETITIONS': return '#04284eff';
      case 'EVENTS': return '#04284eff';
      case 'WORKSHOPS': return '#04284eff';
      case 'MEETINGS': return '#04284eff';
      default: return '#04284eff';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatEventDateTime = (announcement) => {
    if (announcement.eventDate && announcement.eventTime) {
      // Construct date-time simply for display, or better formatting
      return `${announcement.eventDate} at ${announcement.eventTime}`;
    } else if (announcement.eventDate) {
      return new Date(announcement.eventDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return formatDate(announcement.createdAt);
    }
  };

  const getDateLabel = (announcement) => {
    return announcement.eventDate ? ' Event Date:' : ' Posted:';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API_BASE_URL}/${imagePath.replace(/\\/g, "/")}`;
  };

  // ----------------------------
  // JSX Return
  // ----------------------------
  return (
    <div className="announcement-page">
      <div className="announcement-header-section">
        <NavBar />
        <header className="announcement-header">
          <div className="header-content">
            <h1 className="announcement-title">Techstrike News</h1>
            <p className="announcement-subtitle">The latest insights, events, and updates from the Techstrike ecosystem</p>
            <div className="header-divider"></div>
          </div>
        </header>

        {/* Filter Section Integrated into Header background area */}
        <section className="filter-section">
          <div className="filter-container">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search announcements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>

            <div className="filter-box">
              <span className="filter-label">Filter:</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </section>
      </div>

      <main className="announcement-content">
        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Gathering latest updates...</p>
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="empty-state">
            <h3>No results found</h3>
            <p>Try matching with a different keyword or category.</p>
          </div>
        ) : (
          <div className="announcements-grid">
            {filteredAnnouncements.map((announcement) => (
              <article key={announcement._id} className="announcement-card">
                <div className="card-top-accent" style={{ backgroundColor: getCategoryColor(announcement.category) }}></div>

                {announcement.important && (
                  <div className="flag-important">
                    <span>Priority</span>
                  </div>
                )}

                <div className="announcement-image-wrapper">
                  {announcement.image ? (
                    <img src={getImageUrl(announcement.image)} alt={announcement.title} className="announcement-img" />
                  ) : (
                    <div className="image-placeholder">Techstrike</div>
                  )}
                  <div className="announcement-category-tag" style={{ color: getCategoryColor(announcement.category) }}>
                    {announcement.category}
                  </div>
                </div>

                <div className="announcement-body">
                  <div className="announcement-meta-info">
                    <span className="announcement-author-name">@{announcement.author}</span>
                    <span className="announcement-dot">•</span>
                    <span className="announcement-post-date">{formatEventDateTime(announcement)}</span>
                  </div>

                  <h3 className="announcement-card-title">{announcement.title}</h3>
                  <p className="announcement-card-desc">{announcement.description}</p>
                </div>

                <div className="announcement-card-footer">
                  {/* Optional: Read more link or action */}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Announcement;
